/**
 * SupplierPortal — /suppliers/$id/portal (Supplier S2: self-service view).
 *
 * What a supplier sees when they log into the QWise supplier portal: their
 * qualification standing, the actions QA needs from them (open CAPAs, open audit
 * findings, change notifications under review, upcoming audits), their live KPI
 * scorecard, and their documents on file. Assembled client-side from the
 * existing supplier-detail + findings + change-notification feeds (no new
 * endpoint) — filtered to this one supplier.
 */
import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState } from '@edms/design-system';
import { useSupplierDetailQuery } from '../SupplierDetail/useSupplierDetailQuery';
import { useScnQuery } from '../SupplierChangeNotifications/useScnQuery';
import { useAuditFindingsQuery } from '../SupplierAuditFindings/useAuditFindingsQuery';
import styles from './SupplierPortal.module.css';

interface ActionItem {
  kind: 'CAPA' | 'FINDING' | 'SCN' | 'AUDIT';
  ref: string;
  title: string;
  meta: string;
  severity: 'high' | 'med' | 'low';
}

const BAND_CLASS: Record<string, string> = { GREEN: 'bGreen', AMBER: 'bAmber', RED: 'bRed', UNKNOWN: 'bUnknown' };

export function SupplierPortal() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { t, i18n } = useTranslation();
  const detailQ = useSupplierDetailQuery(id);
  const scnQ = useScnQuery();
  const findingsQ = useAuditFindingsQuery();
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }), [i18n.language]);

  const d = detailQ.data;
  const actions = useMemo<ActionItem[]>(() => {
    if (!d) return [];
    const out: ActionItem[] = [];
    for (const c of d.capas ?? []) {
      if (c.status === 'OPEN' || c.status === 'IN_PROGRESS') {
        out.push({ kind: 'CAPA', ref: c.capa_id, title: c.title, meta: `${c.severity} · ${c.status.replace(/_/g, ' ')}`, severity: c.severity === 'CRITICAL' ? 'high' : c.severity === 'MAJOR' ? 'med' : 'low' });
      }
    }
    for (const f of (findingsQ.data?.items ?? []).filter((x) => x.supplier_id === id && x.status !== 'CLOSED')) {
      out.push({ kind: 'FINDING', ref: f.finding_id, title: f.title, meta: `${f.finding_type} · ${f.severity}${f.overdue ? ' · OVERDUE' : ''}`, severity: f.overdue || f.severity === 'CRITICAL' ? 'high' : 'med' });
    }
    for (const s of (scnQ.data?.items ?? []).filter((x) => x.supplier_id === id && (x.status === 'RECEIVED' || x.status === 'UNDER_ASSESSMENT'))) {
      out.push({ kind: 'SCN', ref: s.scn_id, title: s.title, meta: `${s.change_type.replace(/_/g, ' ')} · ${s.status.replace(/_/g, ' ')}`, severity: 'med' });
    }
    for (const a of (d.audits ?? []).filter((x) => x.status === 'PLANNED')) {
      out.push({ kind: 'AUDIT', ref: a.audit_id, title: t('portal.upcomingAudit', { defaultValue: 'Upcoming audit' }), meta: `${dateFmt.format(new Date(a.scheduled_for_utc))} · ${a.auditor}`, severity: 'low' });
    }
    return out;
  }, [d, findingsQ.data, scnQ.data, id, t, dateFmt]);

  const loading = detailQ.isLoading;

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('portal.eyebrow', { defaultValue: 'Supplier portal' })}
        title={d ? d.name : t('portal.loading', { defaultValue: 'Supplier portal' })}
        subtitle={t('portal.subtitle', { defaultValue: 'Your qualification standing, the actions we need from you, your KPIs and documents on file — your self-service view into the QWise quality system.' })}
      />

      {loading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0,1,2,3].map((i)=><Skeleton key={i} height={104} radius={14} />)}</div>
          <Skeleton height={220} radius={14} />
        </>
      )}
      {detailQ.isError && <p className={styles.error} role="alert">{t('portal.error', { defaultValue: 'Could not load the supplier portal.' })}</p>}

      {d && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('portal.kpi.status', { defaultValue: 'Qualification status' })} value={d.lifecycle_status.replace(/_/g, ' ')} accent="var(--spectro-purple, #9333EA)" />
            <StatCard label={t('portal.kpi.score', { defaultValue: 'Quality score' })} value={d.aggregate_quality_score ?? 0} accent="var(--spectro-blue, #0065FF)" deltaCaption={d.risk_category ? t(`supplier.risk.${d.risk_category}`, { defaultValue: d.risk_category }) + ' risk' : undefined} />
            <StatCard label={t('portal.kpi.actions', { defaultValue: 'Actions required' })} value={actions.length} accent={actions.length > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'} deltaCaption={actions.length === 0 ? t('portal.kpi.allClear', { defaultValue: 'all clear' }) : undefined} />
            <StatCard label={t('portal.kpi.requal', { defaultValue: 'Next requalification' })} value={d.next_requalification_at_utc ? dateFmt.format(new Date(d.next_requalification_at_utc)) : '—'} accent="var(--spectro-violet, #3F3DFF)" />
          </Stagger>

          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>{t('portal.actionsTitle', { defaultValue: 'Action required from you' })}</h2>
              <span className={styles.cardSub}>{t('portal.actionsSub', { defaultValue: 'Open items QA needs you to respond to' })}</span>
            </header>
            {actions.length === 0 ? (
              <EmptyState compact icon="✓" title={t('portal.noActions', { defaultValue: 'Nothing outstanding — you are all caught up' })} />
            ) : (
              <ul className={styles.actionList}>
                {actions.map((a) => (
                  <li key={`${a.kind}-${a.ref}`} className={styles.actionRow}>
                    <span className={`${styles.dot} ${a.severity === 'high' ? styles.dHigh : a.severity === 'med' ? styles.dMed : styles.dLow}`} aria-hidden="true" />
                    <span className={styles.actKind}>{a.kind}</span>
                    <span className={styles.actRef}>{a.ref}</span>
                    <span className={styles.actTitle}>{a.title}</span>
                    <span className={styles.actMeta}>{a.meta}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className={styles.twoCol}>
            <section className={styles.card}>
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{t('portal.kpisTitle', { defaultValue: 'Your KPIs' })}</h2>
              </header>
              {(d.kpis ?? []).length === 0 ? (
                <p className={styles.muted}>{t('portal.noKpis', { defaultValue: 'No KPI snapshot yet.' })}</p>
              ) : (
                <ul className={styles.kpiList}>
                  {d.kpis.map((k) => (
                    <li key={k.key} className={styles.kpiRow}>
                      <span className={`${styles.band} ${styles[BAND_CLASS[k.band] ?? '']}`} aria-hidden="true" />
                      <span className={styles.kpiLabel}>{k.label}</span>
                      <span className={styles.kpiVal}>{k.value ?? '—'}{k.unit_kind === 'PERCENTAGE' ? '%' : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.card}>
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{t('portal.docsTitle', { defaultValue: 'Your documents on file' })}</h2>
              </header>
              {(d.documents ?? []).length === 0 ? (
                <p className={styles.muted}>{t('portal.noDocs', { defaultValue: 'No documents on file.' })}</p>
              ) : (
                <ul className={styles.docList}>
                  {d.documents.map((doc) => (
                    <li key={doc.document_id} className={styles.docRow}>
                      <span className={styles.docKind}>{doc.kind.replace(/_/g, ' ').toLowerCase()}</span>
                      <span className={styles.docTitle}>{doc.title}</span>
                      <span className={styles.docMeta}>
                        {doc.expires_at_utc ? t('portal.expires', { defaultValue: 'expires {{d}}', d: dateFmt.format(new Date(doc.expires_at_utc)) }) : doc.state}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <footer className={styles.backLink}>
            <Link to="/suppliers/$id" params={{ id }}>{t('portal.backToDossier', { defaultValue: '← Back to the internal supplier dossier' })}</Link>
          </footer>
        </>
      )}
    </div>
  );
}
